import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredComics, latestComics } from '@/mocks/comics';

const categoryInfo: Record<string, { name: string; icon: string; description: string }> = {
  romance: { name: '恋爱浪漫', icon: 'ri-heart-line', description: '甜蜜心动，感受爱情的美好与悸动' },
  fantasy: { name: '奇幻冒险', icon: 'ri-magic-line', description: '穿越次元，开启不可思议的奇幻之旅' },
  action: { name: '热血动作', icon: 'ri-fire-line', description: '燃爆热血，感受战斗的激情与力量' },
  drama: { name: '都市剧情', icon: 'ri-building-line', description: '都市人生，品味现实中的人情冷暖' },
  comedy: { name: '搞笑日常', icon: 'ri-emotion-laugh-line', description: '轻松一笑，治愈生活中的所有不开心' },
  thriller: { name: '悬疑惊悚', icon: 'ri-ghost-line', description: '心跳加速，探索悬疑背后的真相' },
  historical: { name: '古风历史', icon: 'ri-ancient-gate-line', description: '穿越古今，领略历史长河中的传奇故事' },
  'slice-of-life': { name: '治愈日常', icon: 'ri-cup-line', description: '温暖人心，在平凡日常中发现美好' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const info = categoryInfo[slug || ''] || { name: '漫画分类', icon: 'ri-book-open-line', description: '探索所有漫画' };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const allComics = [...featuredComics, ...latestComics];
  const filteredComics = slug
    ? allComics.filter((comic) => comic.category === slug)
    : allComics;

  const canonicalUrl = `http://kcptesw.com/category/${slug || ''}`;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{info.name}</span>
            </nav>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${info.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{info.name}韩漫</h1>
            </div>
            <p className="text-sm text-foreground-600 ml-13">{info.description} — 共收录 {filteredComics.length} 部作品</p>

            <div className="flex flex-wrap items-center gap-2 mt-5 ml-13">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="text-sm text-foreground-600">共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品</span>
            <div className="flex items-center gap-2 ml-auto">
              <button className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap">最新</button>
              <button className="px-3 py-1.5 rounded-full bg-background-200/70 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-300">最热</button>
              <button className="px-3 py-1.5 rounded-full bg-background-200/70 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-300">评分</button>
            </div>
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画，请浏览其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-2">
                      {comic.category === 'romance' ? '恋爱' : comic.category === 'action' ? '动作' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'drama' ? '剧情' : comic.category === 'comedy' ? '搞笑' : comic.category === 'thriller' ? '悬疑' : comic.category === 'historical' ? '古风' : '治愈'}
                    </span>
                    <h3 className="font-heading text-base font-bold text-foreground-950 line-clamp-1">{comic.title}</h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-500 text-[10px]"></i>{comic.rating}</span>
                      <span>{comic.chapters}话</span>
                      <span>{'status' in comic ? comic.status : '连载中'}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `${info.name}韩漫 - 韩漫网`,
        'description': info.description,
        'url': canonicalUrl,
        'isPartOf': {
          '@type': 'WebSite',
          'name': '韩漫网',
          'url': 'http://kcptesw.com/',
        },
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://kcptesw.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': info.name, 'item': canonicalUrl },
          ],
        },
      }) }} />
    </>
  );
}